// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useState } from 'react'
import { defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import STORAGE_INTEGRATION from '@console/constants/storage-integration'

import Link from '@ttn-lw/components/link'
import Status from '@ttn-lw/components/status'
import Switch from '@ttn-lw/components/switch'
import ErrorNotification from '@ttn-lw/components/error-notification'
import Notification from '@ttn-lw/components/notification'

import Message from '@ttn-lw/lib/components/message'

import MessageStorageTable from '@console/containers/message-storage-table/index.tti'
import MessageStorageCommands from '@console/containers/message-storage-commands/index.tti'
import useMessageStorage from '@console/containers/application-message-storage/use-message-storage.tti'

import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'
import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'

import {
  deleteAppPkgDefaultAssoc,
  setAppPkgDefaultAssoc,
} from '@console/store/actions/application-packages'

import { selectApplicationPackageDefaultAssociation } from '@console/store/selectors/application-packages'

const m = defineMessages({
  currentlyActivated: '(currently activated)',
  currentlyDeactivated: '(currently deactivated)',
  activate: 'Activate message storage',
  infoNotificationContent:
    'Since a lot of data can be retrieved causing poor performance, we only fetch the first 10 items from the chosen start period in the console.',
  downloadMessages: 'Download messages',
})

const ApplicationMessageStorageContainer = ({ appId }) => {
  const currentPackage = useSelector(state =>
    selectApplicationPackageDefaultAssociation(state, STORAGE_INTEGRATION.DEFAULT_PORT),
  )
  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState(undefined)
  const dispatch = useDispatch()
  const isActivated = Boolean(currentPackage)
  const {
    handleDeviceIdChange,
    handleDateChange,
    selectedDevice,
    date,
    dateOptions,
    deviceIdOptions,
  } = useMessageStorage()

  const handleActivateClick = useCallback(async () => {
    try {
      setError(undefined)
      await dispatch(
        attachPromise(
          setAppPkgDefaultAssoc(appId, STORAGE_INTEGRATION.DEFAULT_PORT, {
            package_name: STORAGE_INTEGRATION.DEFAULT_PACKAGE_NAME,
          }),
        ),
      )
    } catch (error) {
      setError(error)
    } finally {
      setFetching(false)
    }
  }, [appId, dispatch])

  const handleDeactivateClick = useCallback(async () => {
    try {
      setError(undefined)
      await dispatch(
        attachPromise(deleteAppPkgDefaultAssoc(appId, STORAGE_INTEGRATION.DEFAULT_PORT)),
      )
    } catch (error) {
      setError(error)
    } finally {
      setFetching(false)
    }
  }, [appId, dispatch])

  return (
    <>
      <div className="d-flex al-center gap-cs-s mt-cs-m">
        <Message content={sharedMessages.status} component="h2" className="m-0" />
        <Status status={isActivated ? 'good' : 'bad'} big pulse={isActivated} />
        <Message
          content={isActivated ? m.currentlyActivated : m.currentlyDeactivated}
          component="h3"
          className="m-0 c-text-neutral-light fw-normal"
        />
      </div>
      <label className="d-flex al-center">
        <Message content={m.activate} component="span" className="mr-cs-xs" />
        <Switch
          onChange={isActivated ? handleDeactivateClick : handleActivateClick}
          checked={isActivated}
          disabled={fetching}
        />
      </label>
      {error && <ErrorNotification error={error} small className="mt-cs-xxs" />}

      {isActivated && (
        <div className="d-flex direction-column gap-ls-m mt-ls-m">
          <Notification content={m.infoNotificationContent} info small className="mb-0" />
          <MessageStorageTable
            appId={appId}
            handleDeviceIdChange={handleDeviceIdChange}
            handleDateChange={handleDateChange}
            selectedDevice={selectedDevice}
            date={date}
            dateOptions={dateOptions}
            deviceIdOptions={deviceIdOptions}
          />
          <MessageStorageCommands
            appId={appId}
            devId={selectedDevice.id !== 'all' ? selectedDevice.id : undefined}
            date={date}
          />

          <div>
            <Message
              component="h2"
              content={sharedMessages.furtherResources}
              className="mt-0 mb-cs-xs"
            />
            <Link.DocLink path="/integrations/storage" secondary>
              <Message content={sharedMessages.storageIntegration} />
            </Link.DocLink>
            {' | '}
            <Link.DocLink path="/reference/application-packages" secondary>
              <Message content={sharedMessages.applicationPackages} />
            </Link.DocLink>
            {' | '}
            <Link.DocLink path="/integrations/storage/retrieve" secondary>
              <Message content={m.downloadMessages} />
            </Link.DocLink>
          </div>
        </div>
      )}
    </>
  )
}

ApplicationMessageStorageContainer.propTypes = {
  appId: PropTypes.string.isRequired,
}

export default ApplicationMessageStorageContainer
